import { useEffect, useState } from 'react';

// LE MISURE DELLA SCENA, lette dalla scena e non dal wrapper.
//
// SI CONTA CIÒ CHE SI VEDE: le mesh reali, accese e visibili. Ground, gizmo, assi e nodi
// d'ambiente li esclude `isRealMesh`; un oggetto spento con l'occhio esce dai conti come esce dal
// palco. Serve anche `isEnabled()` perché l'albero spegne con `setEnabled`, e l'occhio con
// `isVisible`: sono due strade per la stessa cosa. Ingombro, triangoli e materiali raccontano
// così la stessa scena.
//
// NON SI LEGGE `modelMeasurements`: nessuno lo imposta, e il salvataggio lo usa per decidere se le
// misure AR ci sono. Riempirlo da qui farebbe passare quel controllo sempre.
//
// SI RICALCOLA DOPO OGNI FRAME DISEGNATO, al più ogni 200 ms. Il motore disegna solo quando
// qualcosa cambia, quindi il frame è l'unico segnale che non manca mai: il gizmo durante il
// trascinamento, i campi dell'Inspector, l'occhio, l'explode animato — molti di questi non
// mandano alcun evento. Il calcolo parte DOPO il frame, quando le matrici sono già fresche, e non
// ne forza il ricalcolo: `getWorldExtends` lo forza su ogni mesh, e un'orbita lo pagherebbe per
// niente.
export type SceneStats = {
    vertices: number;
    triangles: number;
    materials: number;
    /** Larghezza, altezza e profondità in unità della scena, che sono metri. Senza niente di visibile non c'è. */
    size: { x: number; y: number; z: number } | null;
};

const EMPTY: SceneStats = { vertices: 0, triangles: 0, materials: 0, size: null };

const THROTTLE_MS = 200;

const readStats = (sceneEditor: any): SceneStats => {
    const meshes = sceneEditor?.scene?.meshes;
    if (!meshes) return EMPTY;

    let vertices = 0;
    let triangles = 0;
    const materials = new Set<unknown>();
    const min = { x: Infinity, y: Infinity, z: Infinity };
    const max = { x: -Infinity, y: -Infinity, z: -Infinity };

    for (const mesh of meshes) {
        if (!mesh.isVisible || !mesh.isEnabled() || !sceneEditor.isRealMesh(mesh)) continue;

        // Il nodo radice di un glb è una mesh senza geometria: il suo box sta nell'origine e
        // allargherebbe l'ingombro fino a lì.
        const meshVertices = mesh.getTotalVertices();
        if (!meshVertices) continue;

        // UN'ISTANZA CONTA COME UNA MESH: è quello che la scheda video disegna. Senza indici, i
        // vertici vanno a tre a tre.
        vertices += meshVertices;
        triangles += (mesh.getTotalIndices() || meshVertices) / 3;

        const material = mesh.material;
        if (material) (material.subMaterials ?? [material]).forEach((item: unknown) => item && materials.add(item));

        if (mesh.infiniteDistance) continue;
        const box = mesh.getBoundingInfo().boundingBox;
        for (const axis of ['x', 'y', 'z'] as const) {
            min[axis] = Math.min(min[axis], box.minimumWorld[axis]);
            max[axis] = Math.max(max[axis], box.maximumWorld[axis]);
        }
    }

    return {
        vertices,
        triangles: Math.round(triangles),
        materials: materials.size,
        size: Number.isFinite(min.x) ? { x: max.x - min.x, y: max.y - min.y, z: max.z - min.z } : null
    };
};

const sameStats = (a: SceneStats, b: SceneStats) =>
    a.vertices === b.vertices &&
    a.triangles === b.triangles &&
    a.materials === b.materials &&
    a.size?.x === b.size?.x &&
    a.size?.y === b.size?.y &&
    a.size?.z === b.size?.z;

export const useSceneStats = (sceneEditor: any): SceneStats => {
    const [stats, setStats] = useState<SceneStats>(EMPTY);
    const scene = sceneEditor?.scene;

    useEffect(() => {
        if (!scene) return;

        let timer: ReturnType<typeof setTimeout> | undefined;

        const update = () => {
            timer = undefined;
            const next = readStats(sceneEditor);
            // Un'orbita disegna senza cambiare niente: lo stato resta lo stesso oggetto, e il piede
            // non si ridisegna.
            setStats((previous) => (sameStats(previous, next) ? previous : next));
        };

        const schedule = () => {
            if (timer === undefined) timer = setTimeout(update, THROTTLE_MS);
        };

        update();
        const observer = scene.onAfterRenderObservable.add(schedule);

        return () => {
            scene.onAfterRenderObservable.remove(observer);
            if (timer !== undefined) clearTimeout(timer);
        };
    }, [sceneEditor, scene]);

    return stats;
};
